/**
 * The pages before and around a game, built the way the board is: a travertine
 * slab on the walnut table, carved into areas with engraved titles, and the
 * parchment prompt bar below it saying what the page is waiting for — so the
 * start page and the lobby read as the same table the game is played on.
 */
import type { ReactNode } from 'react';

/**
 * The table, a slab on it, and the prompt bar laid on the wood below the slab.
 * A `narrow` stage is for a page with one thing on it: a smaller table.
 */
export function Stage({
  children,
  prompt,
  narrow = false,
}: {
  children: ReactNode;
  prompt?: ReactNode;
  narrow?: boolean;
}) {
  return (
    <div className={narrow ? 'stage stage-narrow' : 'stage'}>
      <div className="slab">{children}</div>
      {prompt}
    </div>
  );
}

/** A carved recess in the slab, with its engraved title and a note at the far end. */
export function Area({
  title,
  glyph,
  note,
  className,
  children,
}: {
  title: string;
  glyph?: GlyphName;
  note?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={className ? `area ${className}` : 'area'}>
      <h2 className="area-title">
        {glyph && <Glyph name={glyph} />}
        <span>{title}</span>
        {note && <span className="area-note">{note}</span>}
      </h2>
      {children}
    </section>
  );
}

/** The parchment bar under the slab: what this page is, and what it is waiting for. */
export function PromptBar({
  title,
  hint,
  yours = false,
  children,
}: {
  title: string;
  hint: ReactNode;
  /** Ringed in the accent, as the game's bar is when the move is yours. */
  yours?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={yours ? 'stage-prompt prompt-yours' : 'stage-prompt'} role="status">
      <div className="prompt-words">
        <span className="prompt-title">{title}</span>
        <span className="stage-hint">{hint}</span>
      </div>
      {children && <div className="prompt-buttons">{children}</div>}
    </div>
  );
}

/**
 * The board's action glyphs, as SVG for the HTML. The canvas draws the same
 * shapes from the same coordinates in table/draw/icons.ts; keep the two alike.
 */
const GLYPHS = {
  forum: (
    <>
      <path d="M0 -10.5 L11 -3.5 H-11 Z" />
      <rect x={-7.2} y={-2} width={3.2} height={9} />
      <rect x={-1.6} y={-2} width={3.2} height={9} />
      <rect x={4} y={-2} width={3.2} height={9} />
      <rect x={-10} y={8} width={20} height={2.4} />
    </>
  ),
  trajan: (
    <path d="M-10 10.5 V-1 A10 10 0 0 1 10 -1 V10.5 H4.4 V-1 A4.4 4.4 0 0 0 -4.4 -1 V10.5 Z" />
  ),
  senate: (
    <>
      <rect x={-1.4} y={-11} width={2.8} height={7} />
      <rect x={-4.5} y={-4} width={9} height={3} />
      <rect x={-7.5} y={0.5} width={15} height={3} />
      <rect x={-10.5} y={5} width={21} height={3.4} />
    </>
  ),
  seaport: (
    <>
      <path d="M-10 3.5 H10 L7 9.5 H-7 Z" />
      <rect x={-1.2} y={-10} width={2.4} height={13.5} />
      <path d="M2 -9 Q8.5 -4 2 2 Z M-2 -9 Q-8.5 -4 -2 2 Z" />
    </>
  ),
  military: <path d="M0 -10.5 L9.5 -7 V1 Q9.5 7.5 0 11 Q-9.5 7.5 -9.5 1 V-7 Z" />,
  cards: (
    <>
      <rect
        x={-9}
        y={-7}
        width={11}
        height={15}
        rx={1.5}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
      />
      <rect x={-2} y={-9} width={11} height={15} rx={1.5} />
    </>
  ),
  hourglass: (
    <path d="M-7 -11 H7 V-9 Q7 -3 1.5 0 Q7 3 7 9 V11 H-7 V9 Q-7 3 -1.5 0 Q-7 -3 -7 -9 Z" />
  ),
} as const;

export type GlyphName = keyof typeof GLYPHS;

export function Glyph({ name }: { name: GlyphName }) {
  return (
    <svg className="glyph" viewBox="-12 -12 24 24" fill="currentColor" aria-hidden="true">
      {GLYPHS[name]}
    </svg>
  );
}

/** A seat's painted token, in the colours the table gives it. */
export const SeatDot = ({ seat }: { seat: number }) => (
  <span className={`seat-dot seat-dot-${seat}`} aria-hidden="true" />
);
